import * as RadixPopover from '@radix-ui/react-popover'
import styled, { keyframes } from 'styled-components'

import type { ClassNameProps } from '@root/shared/react'

export type PopoverProps = {
  children: React.ReactElement
  content: React.ReactNode
} & RadixPopover.PopoverProps &
  Pick<
    RadixPopover.PopoverContentProps,
    'side' | 'sideOffset' | 'align' | 'alignOffset'
  > &
  ClassNameProps

const PopoverRoot = ({
  children,
  content,
  className,
  sideOffset = 8,
  side,
  align,
  alignOffset,
  ...props
}: PopoverProps) => {
  return (
    <RadixPopover.Root {...props}>
      <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <Content
          className={className}
          sideOffset={sideOffset}
          side={side}
          align={align}
          alignOffset={alignOffset}
        >
          {content}
        </Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}

const slideUpAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateY(4px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
`

const slideRightAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateX(-4px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
`

const slideDownAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateY(-4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`

const slideLeftAndFade = keyframes`
	from {
		opacity: 0;
		transform: translateX(4px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
`

const Content = styled(RadixPopover.Content)`
  background: var(--background-3);
  border: 1px solid var(--backgroundA-3);
  border-radius: 16px;
  box-shadow: 0px 22px 45px 0px rgba(0, 0, 0, 0.37);

  overflow: hidden;
  outline: none;
  min-width: var(--radix-popper-anchor-width);

  animation-duration: 125ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  &[data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`

type Compound = typeof PopoverRoot

const Popover = PopoverRoot as Compound

export { Popover }
